import { Cell, flexRender } from '@tanstack/react-table';
import { MOBILE_SUMMARY_SLOT_MAX_WIDTH } from './constants';

interface MobileSummaryCellProps<TData> {
  /** The cells to render in the summary (first 3 visible columns) */
  cells: Cell<TData, unknown>[];
  /** Calculated width for the summary cell container */
  width: number | null;
  /**
   * Tree-mode indent per depth level, in px. 0 when not in tree mode.
   * Supplied by DataTable from the shared tree geometry so this cell can't
   * drift from the desktop <TreeCellLeading> indent.
   */
  indentPerLevelPx?: number;
  /**
   * Tree-mode leading prefix width (chevron [+ icon]) in px, added to the
   * row-2 pad so its content lines up with the row-1 title. 0 when not tree.
   */
  leadingPrefixPx?: number;
}

/**
 * Mobile summary cell that collapses multiple columns into a stacked layout.
 *
 * Layout:
 * - Row 1: First column content (full width, min-height 28px). When the
 *   first column is a `TreeCellLeading` cell its chevron + icon shift the
 *   title to the right; row 1's content already handles that internally.
 * - Row 2: Second and third columns side-by-side. When the table is in
 *   tree mode the row receives a matching left-pad (depth indent + chevron
 *   + icon prefix) so the row-2 content aligns with the row-1 title
 *   instead of the card edge.
 */
export function MobileSummaryCell<TData>({
  cells,
  width,
  indentPerLevelPx = 0,
  leadingPrefixPx = 0,
}: MobileSummaryCellProps<TData>) {
  if (cells.length === 0) return null;

  // Tree mode is active when DataTable passed a leading prefix. depth is
  // always defined (0 for non-tree rows); indent + prefix come from the
  // shared tree geometry so this can't drift from <TreeCellLeading>.
  const row = cells[0]?.row;
  const rowTwoLeftPad =
    leadingPrefixPx && row ? row.depth * indentPerLevelPx + leadingPrefixPx : 0;

  return (
    <div
      className="flex min-w-0 flex-col gap-1"
      style={width ? { width } : undefined}
    >
      {/* Row 1: First column content (full width) */}
      {cells[0] && (
        <div className="flex min-h-7 min-w-0 items-center">
          {flexRender(cells[0].column.columnDef.cell, cells[0].getContext())}
        </div>
      )}

      {/* Row 2: Second and third column content (side by side, proportional max 60%).
          In tree mode, pad left so the row-2 content lines up with the row-1
          title (past chevron + icon + depth-indent). */}
      {(cells[1] || cells[2]) && (
        <div
          className="flex min-w-0 items-center gap-2"
          style={rowTwoLeftPad ? { paddingLeft: rowTwoLeftPad } : undefined}
        >
          {cells[1] && (
            <div
              className={`-my-0.5 flex min-w-0 ${MOBILE_SUMMARY_SLOT_MAX_WIDTH} flex-auto justify-start`}
            >
              {flexRender(
                cells[1].column.columnDef.cell,
                cells[1].getContext(),
              )}
            </div>
          )}
          {cells[2] && (
            <div
              className={`flex min-w-0 ${MOBILE_SUMMARY_SLOT_MAX_WIDTH} flex-auto justify-end`}
            >
              {flexRender(
                cells[2].column.columnDef.cell,
                cells[2].getContext(),
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
